import { type RefObject, useCallback, useEffect, useState } from 'react';
import type { Measurable } from '@radix-ui/rect';

import { SettingsPopoverShell } from '@/components/shared/settings-popover-shell';
import { Textarea } from '@/components/ui/textarea';
import { MutedText, SmallText } from '@/components/ui/typography';
import { useBlockSettingsState } from '@/hooks/use-block-settings-state';
import { readString } from '@/lib/prop-utils';
import { usePageEditorStore } from '@/stores/page-editor-store';
import type { PageBlock } from '@/types/block';

interface RawHtmlBlockSettingsModalProps {
  sectionId: string;
  block: PageBlock;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  anchorRef?: RefObject<Measurable | null>;
}

export function RawHtmlBlockSettingsModal({
  sectionId,
  block,
  open,
  onOpenChange,
  anchorRef,
}: RawHtmlBlockSettingsModalProps) {
  const updateBlock = usePageEditorStore((state) => state.updateBlock);
  const initialHtml = readString(block.props?.resolvedHtml) ?? readString(block.props?.html) ?? '';
  const [html, setHtml] = useState(initialHtml);

  useEffect(() => {
    if (open) setHtml(initialHtml);
  }, [open, initialHtml]);

  const applyChange = useCallback(
    (value: Record<string, unknown> | null, commit: boolean) => {
      if (!commit) return;
      updateBlock(sectionId, block.id, { props: value ?? {} });
    },
    [sectionId, block.id, updateBlock],
  );

  const { handleSave: saveSettings, handleCancel } = useBlockSettingsState({
    open,
    currentValue: block.props || {},
    applyChange,
    cancelPreview: () => {},
  });

  const handleSave = useCallback(
    () => saveSettings({ html, resolvedHtml: null }),
    [html, saveSettings],
  );

  return (
    <SettingsPopoverShell
      open={open}
      onOpenChange={onOpenChange}
      anchorRef={anchorRef}
      title="HTML Settings"
      onSave={handleSave}
      onCancel={handleCancel}
    >
      <div className="space-y-1.5">
        <SmallText className="font-semibold text-foreground">HTML</SmallText>
        <MutedText className="text-xs">
          Rendered inline in the page — styles and scripts from site settings apply
        </MutedText>
        <Textarea
          value={html}
          onChange={(event) => setHtml(event.target.value)}
          rows={12}
          fieldSizing="fixed"
          className="resize-none font-mono text-sm"
          placeholder="<div>...</div>"
        />
      </div>
    </SettingsPopoverShell>
  );
}
